/**
 * useMemberCalendarMapping Hook
 *
 * Manages calendar mappings from a family member's perspective.
 * Returns which calendars are linked to a specific member
 * and provides methods to link/unlink calendars.
 */

import { useState, useEffect, useCallback } from 'react';
import { useCalendarMemberMappingStore } from './use-scoped-managers';
import { CalendarMappingEvents } from '@/lib/calendar/CalendarMappingEvents';

type UseMemberCalendarMappingResult = {
  linkedCalendarIds: string[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isCalendarLinked: (calendarId: string) => boolean;
  linkCalendar: (calendarId: string) => Promise<void>;
  unlinkCalendar: (calendarId: string) => Promise<void>;
  toggleCalendar: (calendarId: string) => Promise<void>;
};

export function useMemberCalendarMapping(memberId: string | null): UseMemberCalendarMappingResult {
  const store = useCalendarMemberMappingStore();
  const [linkedCalendarIds, setLinkedCalendarIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLinkedCalendars = useCallback(async () => {
    if (!store) {
      return;
    }

    if (!memberId) {
      setLinkedCalendarIds([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const calendarIds = await store.getCalendarsForMember(memberId);
      setLinkedCalendarIds(calendarIds);
    } catch (err) {
      console.error('Error loading member calendar mappings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load calendar mappings');
    } finally {
      setIsLoading(false);
    }
  }, [store, memberId]);

  useEffect(() => {
    if (store) {
      loadLinkedCalendars();
    }
  }, [store, loadLinkedCalendars]);

  const isCalendarLinked = useCallback((calendarId: string): boolean => {
    return linkedCalendarIds.includes(calendarId);
  }, [linkedCalendarIds]);

  const linkCalendar = useCallback(async (calendarId: string): Promise<void> => {
    if (!store || !memberId) return;

    // Get current mapping for this calendar
    const existing = await store.getByCalendarId(calendarId);
    const currentMemberIds = existing?.memberIds || [];

    // Add this member if not already present
    if (!currentMemberIds.includes(memberId)) {
      await store.setMapping(calendarId, [...currentMemberIds, memberId]);
      setLinkedCalendarIds(prev => [...prev, calendarId]);
      CalendarMappingEvents.emitMappingsChanged();
    }
  }, [store, memberId]);

  const unlinkCalendar = useCallback(async (calendarId: string): Promise<void> => {
    if (!store || !memberId) return;

    // Get current mapping for this calendar
    const existing = await store.getByCalendarId(calendarId);
    if (!existing) return;

    // Remove this member from the calendar's member list
    const newMemberIds = existing.memberIds.filter(id => id !== memberId);
    await store.setMapping(calendarId, newMemberIds);
    setLinkedCalendarIds(prev => prev.filter(id => id !== calendarId));
    CalendarMappingEvents.emitMappingsChanged();
  }, [store, memberId]);

  const toggleCalendar = useCallback(async (calendarId: string): Promise<void> => {
    if (isCalendarLinked(calendarId)) {
      await unlinkCalendar(calendarId);
    } else {
      await linkCalendar(calendarId);
    }
  }, [isCalendarLinked, linkCalendar, unlinkCalendar]);

  return {
    linkedCalendarIds,
    isLoading,
    error,
    refresh: loadLinkedCalendars,
    isCalendarLinked,
    linkCalendar,
    unlinkCalendar,
    toggleCalendar,
  };
}
